import { useContext, type ReactNode } from 'react'
import { View, Text, Pressable, Image, useWindowDimensions } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { Instagram, Youtube } from 'lucide-react-native'
import { LanguageContext } from '../../../src/i18n'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'
import { LANDING_SITE_NAV_ROUTES } from '../../../src/landing/constants/landingSiteNav.constants'
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets'
import { landingFooterStyles as styles } from './LandingFooter.styles'

const SOCIAL = [
  { icon: <Instagram size={18} color="#718096" />, label: 'Instagram', href: 'https://www.instagram.com/zporti.app/' },
  { icon: <Youtube size={18} color="#718096" />, label: 'YouTube', href: 'https://youtube.com/@zporti' },
]

const FOOTER_STACKED_WIDTH = 760
const FOOTER_PHONE_WIDTH = LANDING_PHONE_MAX_WIDTH

function FooterSectionShell({
  isPhone,
  isLast,
  children,
}: {
  isPhone: boolean
  isLast?: boolean
  children: ReactNode
}) {
  if (!isPhone) return <>{children}</>
  return (
    <View style={[styles.footSectionShell, isLast && styles.footSectionShellLast]}>{children}</View>
  )
}

interface LandingFooterProps {
  onPressPrivacy: () => void
  onPressTerms: () => void
  onPressDataDeletion: () => void
  onPressCredits: () => void
  onPressPrivacyControls?: () => void
  onPressContact: () => void
  onPressSupport?: () => void
  onPressFeatures: () => void
  onPressPricing: () => void
}

export function LandingFooter({
  onPressPrivacy,
  onPressTerms,
  onPressDataDeletion,
  onPressCredits,
  onPressPrivacyControls,
  onPressContact,
  onPressSupport,
  onPressFeatures,
  onPressPricing,
}: LandingFooterProps) {
  const router = useRouter()
  const { width } = useWindowDimensions()
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingFooter must be used within LanguageProvider')
  }
  const { t } = languageContext
  /** Until layout reports a width (e.g. first paint / SSR), assume a narrow viewport so we stack instead of row + overlap. */
  const layoutWidth = width > 0 ? width : 390
  const isStacked = layoutWidth < FOOTER_STACKED_WIDTH
  const isPhone = layoutWidth < FOOTER_PHONE_WIDTH
  const PRODUCT_LINKS = [
    { label: t.features.title, onPress: onPressFeatures },
    { label: t.packsPage.pageTitle, onPress: onPressPricing },
  ]
  const COMPETE_LINKS = [
    {
      label: t.siteNav.organizations,
      onPress: () => router.push(LANDING_SITE_NAV_ROUTES.ORGANIZATIONS as Href),
    },
    {
      label: t.siteNav.openApp,
      onPress: () => router.push(LANDING_SITE_NAV_ROUTES.APP as Href),
    },
  ]

  return (
    <View style={[styles.footer, isStacked && styles.footerStacked]}>
      <View style={styles.container}>
        <View style={[styles.footGrid, isStacked && styles.footGridStacked, isPhone && styles.footGridPhone]}>
          <FooterSectionShell isPhone={isPhone}>
            <View style={[styles.footBrand, isStacked && styles.footBrandStacked, isPhone && styles.footBrandPhone]}>
              <View style={styles.logoRow}>
                <View style={styles.logoMark}>
                  <Image
                    source={{ uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_LOGO) }}
                    style={styles.logoMarkImage}
                    resizeMode="cover"
                    accessibilityIgnoresInvertColors
                  />
                </View>
                <Text style={styles.logoText}>zporti</Text>
              </View>
              <Text style={[styles.brandTagline, isPhone && styles.brandTaglinePhone]}>
                {isPhone ? t.footer.phoneTagline : t.hero.subtitle}
              </Text>
              <View style={styles.socialRow}>
                {SOCIAL.map((s) => (
                  <Pressable
                    key={s.label}
                    accessibilityRole="link"
                    accessibilityLabel={s.label}
                    onPress={() => {
                      if (typeof window !== 'undefined') {
                        window.open(s.href, '_blank', 'noopener,noreferrer')
                      }
                    }}
                    style={({ hovered }) => [styles.socialBtn, hovered && styles.socialBtnHovered]}
                  >
                    {s.icon}
                  </Pressable>
                ))}
              </View>
            </View>
          </FooterSectionShell>

          <FooterSectionShell isPhone={isPhone}>
            <View style={[styles.footCol, isStacked && styles.footColStacked, isPhone && styles.footColPhone]}>
              <Text style={styles.footColH4}>{t.footer.product}</Text>
              {PRODUCT_LINKS.map((link) => (
                <Pressable
                  key={link.label}
                  accessibilityRole="link"
                  onPress={link.onPress}
                  style={styles.footLink}
                >
                  {({ hovered }) => (
                    <Text style={[styles.footLinkText, hovered && styles.footLinkTextHovered]}>
                      {link.label}
                    </Text>
                  )}
                </Pressable>
              ))}
            </View>
          </FooterSectionShell>

          <FooterSectionShell isPhone={isPhone}>
            <View style={[styles.footCol, isStacked && styles.footColStacked, isPhone && styles.footColPhone]}>
              <Text style={styles.footColH4}>{t.footer.compete}</Text>
              {COMPETE_LINKS.map((link) => (
                <Pressable
                  key={link.label}
                  accessibilityRole="link"
                  onPress={link.onPress}
                  style={styles.footLink}
                >
                  {({ hovered }) => (
                    <Text style={[styles.footLinkText, hovered && styles.footLinkTextHovered]}>
                      {link.label}
                    </Text>
                  )}
                </Pressable>
              ))}
            </View>
          </FooterSectionShell>

          <FooterSectionShell isPhone={isPhone} isLast>
            <View style={[styles.footCol, isStacked && styles.footColStacked, isPhone && styles.footColPhone]}>
              <Text style={styles.footColH4}>{t.footer.company}</Text>
              <Pressable
                accessibilityRole="link"
                onPress={onPressContact}
                style={styles.footLink}
              >
                {({ hovered }) => (
                  <Text style={[styles.footLinkText, hovered && styles.footLinkTextHovered]}>
                    {t.siteNav.contact}
                  </Text>
                )}
              </Pressable>
              {onPressSupport ? (
                <Pressable
                  accessibilityRole="link"
                  onPress={onPressSupport}
                  style={styles.footLink}
                >
                  {({ hovered }) => (
                    <Text style={[styles.footLinkText, hovered && styles.footLinkTextHovered]}>
                      Support
                    </Text>
                  )}
                </Pressable>
              ) : null}
            </View>
          </FooterSectionShell>
        </View>

        <View style={styles.divider} />

        <View style={[styles.footBottom, isStacked && styles.footBottomStacked, isPhone && styles.footBottomPhone]}>
          <Text style={[styles.copyright, isStacked && styles.copyrightStacked, isPhone && styles.copyrightPhone]}>
            {isPhone ? t.footer.copyrightShort : t.footer.copyright}
          </Text>
          <View style={[styles.legalLinks, isStacked && styles.legalLinksStacked, isPhone && styles.legalLinksPhone]}>
            <Pressable
              accessibilityRole="link"
              onPress={onPressPrivacy}
              style={styles.legalLink}
            >
              {({ hovered }) => (
                <Text style={[styles.legalLinkText, hovered && styles.legalLinkTextHovered]}>
                  {t.footer.privacyPolicy}
                </Text>
              )}
            </Pressable>
            <Pressable
              accessibilityRole="link"
              onPress={onPressTerms}
              style={styles.legalLink}
            >
              {({ hovered }) => (
                <Text style={[styles.legalLinkText, hovered && styles.legalLinkTextHovered]}>
                  {t.footer.termsOfService}
                </Text>
              )}
            </Pressable>
            <Pressable
              accessibilityRole="link"
              onPress={onPressDataDeletion}
              style={styles.legalLink}
            >
              {({ hovered }) => (
                <Text style={[styles.legalLinkText, hovered && styles.legalLinkTextHovered]}>
                  {t.footer.dataDeletion}
                </Text>
              )}
            </Pressable>
            <Pressable
              accessibilityRole="link"
              onPress={onPressCredits}
              style={styles.legalLink}
            >
              {({ hovered }) => (
                <Text style={[styles.legalLinkText, hovered && styles.legalLinkTextHovered]}>
                  {t.footer.credits}
                </Text>
              )}
            </Pressable>
            {onPressPrivacyControls ? (
              <Pressable
                accessibilityRole="link"
                onPress={onPressPrivacyControls}
                style={styles.legalLink}
              >
                {({ hovered }) => (
                  <Text style={[styles.legalLinkText, hovered && styles.legalLinkTextHovered]}>
                    {t.footer.privacyControls}
                  </Text>
                )}
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  )
}
